import secureApiService from './secureApiService'
import {
  CreateQuizPayload,
  CreateQuizQuestionPayload,
  ManualGradeAttemptPayload,
  Quiz,
  QuizAttempt,
  QuizAttemptAnswerMap,
  QuizAttemptListResponse,
  QuizAttemptMeta,
  QuizAttemptQueryParams,
  QuizAttemptAnswersPayload,
  QuizListResponse,
  QuizQuestion,
  QuizQuestionListResponse,
  QuizQueryParams,
  StartQuizAttemptResponse,
  SubmitQuizAttemptPayload,
  SubmitQuizAttemptResult,
  UpdateQuizPayload,
  UpdateQuizQuestionPayload,
} from '@/types/quizzes'
import {
  QuizModel,
  QuizAttemptModel,
  mapAttemptList,
  mapQuizList,
  mapQuizQuestion,
  normalizeQuizQueryParams,
  sanitizeManualGradePayload,
  sanitizeQuestionPayload,
} from '@/models/quiz.model'
import { normalizePaginationMeta } from '@/models/templates/pagination.model'

type ApiEnvelope<T> = {
  success?: boolean
  message?: string
  data: T
  meta?: Record<string, unknown>
}

const unwrap = <T>(payload: ApiEnvelope<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as ApiEnvelope<T>)) {
    return (payload as ApiEnvelope<T>).data
  }
  return payload as T
}

const extractMeta = (payload: unknown): Record<string, unknown> | undefined => {
  if (!payload || typeof payload !== 'object') return undefined
  const value = payload as Record<string, unknown>
  if (value.meta && typeof value.meta === 'object') {
    return value.meta as Record<string, unknown>
  }
  if (value.data && typeof value.data === 'object') {
    const nested = value.data as Record<string, unknown>
    if (nested.meta && typeof nested.meta === 'object') {
      return nested.meta as Record<string, unknown>
    }
  }
  return undefined
}

const toQueryString = (params?: URLSearchParams): string => {
  if (!params) return ''
  const value = params.toString()
  return value ? `?${value}` : ''
}

class QuizService {
  private readonly baseUrl = '/quizzes'

  async getQuizzes(params?: QuizQueryParams): Promise<QuizListResponse> {
    const search = normalizeQuizQueryParams(params)
    const response = await secureApiService.get<any>(`${this.baseUrl}${toQueryString(search)}`)
    const payload = response.data
    const quizzes = mapQuizList(payload)
    const meta = extractMeta(payload)
    return { quizzes, meta }
  }

  async getQuiz(quizId: string): Promise<QuizModel | null> {
    const response = await secureApiService.get<any>(`${this.baseUrl}/${quizId}`)
    const payload = unwrap(response.data)
    if (!payload) return null
    return QuizModel.fromJSON(payload as Record<string, unknown>)
  }

  async createQuiz(payload: CreateQuizPayload): Promise<QuizModel> {
    const response = await secureApiService.post<any>(this.baseUrl, payload)
    const data = unwrap(response.data)
    return QuizModel.fromJSON(data as Record<string, unknown>)
  }

  async updateQuiz(payload: UpdateQuizPayload): Promise<QuizModel> {
    const { id, ...body } = payload
    const response = await secureApiService.put<any>(`${this.baseUrl}/${id}`, body)
    const data = unwrap(response.data)
    return QuizModel.fromJSON(data as Record<string, unknown>)
  }

  async deleteQuiz(quizId: string): Promise<boolean> {
    await secureApiService.delete(`${this.baseUrl}/${quizId}`)
    return true
  }

  async getQuizQuestions(quizId: string): Promise<QuizQuestionListResponse> {
    const response = await secureApiService.get<any>(`${this.baseUrl}/${quizId}/questions`)
    const payload = unwrap(response.data)
    const quizData = payload?.quiz ?? payload?.data?.quiz ?? null
    const questionsRaw = payload?.questions ?? payload?.data?.questions ?? []

    const quiz = quizData ? QuizModel.fromJSON(quizData) : null
    const questions: QuizQuestion[] = Array.isArray(questionsRaw)
      ? questionsRaw.map((item: any) => mapQuizQuestion(item))
      : []

    return {
      quiz: quiz
        ? {
            id: quiz.id,
            title: quiz.title,
            slug: quiz.slug,
            description: quiz.description,
            timeLimitSeconds: quiz.timeLimitSeconds ?? undefined,
            maxAttempts: quiz.maxAttempts ?? undefined,
          }
        : {
            id: quizId,
            title: '',
            slug: '',
            description: '',
            timeLimitSeconds: undefined,
            maxAttempts: undefined,
          },
      questions,
    }
  }

  async createQuestion(quizId: string, payload: CreateQuizQuestionPayload): Promise<QuizQuestion> {
    const sanitized = sanitizeQuestionPayload(payload)
    const response = await secureApiService.post<any>(
      `${this.baseUrl}/${quizId}/questions`,
      sanitized,
    )
    const data = unwrap(response.data)
    return mapQuizQuestion(data as Record<string, unknown>)
  }

  async updateQuestion(
    quizId: string,
    questionId: string,
    payload: UpdateQuizQuestionPayload,
  ): Promise<QuizQuestion> {
    const sanitized = sanitizeQuestionPayload(payload)
    const response = await secureApiService.put<any>(
      `${this.baseUrl}/${quizId}/questions/${questionId}`,
      sanitized,
    )
    const data = unwrap(response.data)
    return mapQuizQuestion(data as Record<string, unknown>)
  }

  async deleteQuestion(quizId: string, questionId: string): Promise<boolean> {
    await secureApiService.delete(`${this.baseUrl}/${quizId}/questions/${questionId}`)
    return true
  }

  async getAttempts(quizId: string, params?: QuizAttemptQueryParams): Promise<QuizAttemptListResponse> {
    const search = normalizeQuizQueryParams(params)
    const response = await secureApiService.get<any>(
      `${this.baseUrl}/${quizId}/attempts${toQueryString(search)}`,
    )
    const payload = response.data
    const attempts = mapAttemptList(payload)
    const metaRecord = extractMeta(payload)
    const meta = normalizePaginationMeta(metaRecord)
    return { attempts, meta }
  }

  async getAttempt(quizId: string, attemptId: string): Promise<QuizAttemptModel | null> {
    const response = await secureApiService.get<any>(
      `${this.baseUrl}/${quizId}/attempts/${attemptId}`,
    )
    const data = unwrap(response.data)
    if (!data) return null
    return QuizAttemptModel.fromJSON(data as Record<string, unknown>)
  }

  async startAttempt(
    quizId: string,
    payload?: Partial<Pick<QuizAttempt, 'attempt' | 'startedAt'>>,
  ): Promise<StartQuizAttemptResponse> {
    const response = await secureApiService.post<any>(
      `${this.baseUrl}/${quizId}/attempts`,
      payload,
    )
    const data = unwrap(response.data)
    const attemptData = data?.attempt ?? data?.data?.attempt ?? data
    const quizData = data?.quiz ?? data?.data?.quiz ?? null
    const questionsRaw = data?.questions ?? data?.data?.questions ?? []

    const attemptModel = QuizAttemptModel.fromJSON(attemptData as Record<string, unknown>)
    const quizModel =
      quizData
        ? QuizModel.fromJSON(quizData as Record<string, unknown>)
        : await this.getQuiz(quizId)
    const fallbackQuiz =
      quizModel ??
      new QuizModel({
        id: quizId,
        courseId: '',
        title: '',
        slug: null,
        description: null,
        isActive: true,
        timeLimitSeconds: null,
        maxAttempts: null,
        allowDevices: null,
        requireProctoring: null,
        dueDate: null,
        createdBy: null,
        createdAt: null,
        updatedAt: null,
        questions: [],
      } as Quiz)

    return {
      attempt: attemptModel,
      quiz: fallbackQuiz,
      questions: Array.isArray(questionsRaw)
        ? questionsRaw.map((item: any) => mapQuizQuestion(item))
        : [],
    }
  }

  async submitAttempt(payload: SubmitQuizAttemptPayload): Promise<SubmitQuizAttemptResult> {
    const { quizId, attemptId, answers, meta, files } = payload
    const formData = new FormData()
    formData.append('answers', JSON.stringify(answers))
    if (meta && Object.keys(meta).length > 0) {
      formData.append('meta', JSON.stringify(meta))
    }
    if (files?.length) {
      files.forEach((file) => {
        formData.append('files', file)
      })
    }
    const response = await secureApiService.postFormData<any>(
      `${this.baseUrl}/${quizId}/attempts/${attemptId}/submit`,
      formData,
    )
    const data = unwrap(response.data)
    return {
      attemptId: String(data?.attemptId ?? attemptId),
      uploadedFiles: Array.isArray(data?.uploadedFiles)
        ? data.uploadedFiles.map((file: Record<string, unknown>) => ({
            id: String(file.id ?? file.fileId ?? ''),
            attemptId: String(file.attempt_id ?? file.attemptId ?? attemptId),
            fileName: String(file.file_name ?? file.fileName ?? file.filename ?? 'file'),
            url: String(file.url ?? ''),
            storageKey: file.storage_key ?? file.storageKey ?? null,
            size: Number(file.size ?? file.file_size ?? file.fileSize ?? 0),
            mimeType: file.mime_type ?? file.mimeType ?? null,
            uploadedAt: file.uploaded_at ?? file.uploadedAt ?? null,
            createdAt: file.created_at ?? file.createdAt ?? null,
            updatedAt: file.updated_at ?? file.updatedAt ?? null,
          }))
        : undefined,
      grading: data?.grading ?? undefined,
    }
  }

  async gradeAttempt(payload: ManualGradeAttemptPayload): Promise<QuizAttemptModel> {
    const sanitized = sanitizeManualGradePayload(payload)
    const response = await secureApiService.post<any>(
      `${this.baseUrl}/${payload.quizId}/attempts/${payload.attemptId}/grade`,
      sanitized,
    )
    const data = unwrap(response.data)
    return QuizAttemptModel.fromJSON(data as Record<string, unknown>)
  }

  async deleteAttempt(quizId: string, attemptId: string): Promise<boolean> {
    await secureApiService.delete(`${this.baseUrl}/${quizId}/attempts/${attemptId}`)
    return true
  }

  buildAttemptAnswersPayload(
    attempt: QuizAttemptModel,
    questions: QuizQuestion[],
    responses: QuizAttemptAnswerMap[],
    submittedAt?: string,
    durationSeconds?: number,
    meta?: QuizAttemptMeta,
  ): QuizAttemptAnswersPayload {
    return {
      quizId: attempt.quizId,
      attempt: attempt.attempt,
      startedAt: attempt.startedAt,
      submittedAt,
      durationSeconds,
      questions: responses.map((item) => ({
        id: item.id,
        type: item.type,
        answer: item.answer ?? null,
      })),
      meta: meta ?? undefined,
    }
  }
}

export const quizService = new QuizService()
